import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as translator from "../../utils/translator";
import { getForwardableFunnelSearch } from "../../utils/funnelSearchParams";
import { getCartMentionQuery, getFunnelSplitRedirectUrl } from "../../utils/funnelMentionRedirects";
import * as http from "../../utils/http";
import * as SocialMediaTracker from "../../PixelTrackers/SocialMediaTracker";
import HeaderController from "../../components/Headers/HeaderController";
import ForgotPasswordModal from "../../components/Modals/ForgotPasswordModal/ForgotPasswordModal";
import LoadingModal from "../../components/Modals/LoadingModal/LoadingModal";
import ServerError from "../../components/ServerError/ServerError";
import SubmitButton from "../../components/SubmitButton/SubmitButton";
import Question from "./components/Question/Question";
import { MainTracker } from "../../PixelTrackers/MainTracker";
import { track as trackGTM } from "../../PixelTrackers/GtmTracker";
import LoginQuestion from "./components/LoginQuestion/LoginQuestion";
import "./Questionnaire.css"
import "./components/Question/Question.css";
import "./components/SubQuestion/SubQuestion.css";
import {
  bundleQuestionnaireForSending,
  formatQuestionnaire,
  isQuestionaireBlocking,
} from "./components/helpers";
import { useQuestionnaire } from "./customHooks/useQuestionnaire";
import StickyNotification from "../../components/StickyNotification/StickyNotification";
import Footer from "../../components/Footer/Footer";
import EmojiBlock from "./components/EmojiBlock/EmojiBlock";
import DarkLoadingModal from "../../components/Modals/DarkLoadingModal/DarkLoadingModal";
import { SkipQuestionnaireModal } from "../../components/Modals/SkipQuestionnaireModal/SkipQuestionnaireModal";
import { useCartContext } from "../../customHooks/useCart";
import { QUESTIONNAIRE_PAGE_VIEW_EVENT } from "../../utils/consts";
import { useCustomer } from "../../customHooks/useCustomer";

import { calculateBMI, extractBmiThresholds } from "./components/BMISection/BMISection";
import { assignSaveAnswerVariant, getSaveAnswerVariant, SaveAnswerVariant } from "../../utils/saveQuestionnaireAnswersExperiment";
import { registerWlExperimentSuperProperties } from "../../utils/wlCarePlusExperiment";
import { registerCustomerStatus, registerFunnelEntry, registerSplitFunnelVariant } from "../../utils/wlFunnelExperiment";
import { getFlowzzTrackingProps } from "../../utils/flowzzTracking";
import QuestionnaireVariantB from "./VariantB/QuestionnaireVariantB";
import {
  AnswerTreeNode,
  AnswerValue,
  computeRestorePlan,
  FlattenedQuestion,
  markPendingClear,
  saveQuestionnaireAnswers,
  serializeQuestionnaireAnswers,
} from "../../utils/QuestionnaireAnswersStorage";


let dangerSignificancesRefs: any = []; // mainly for scrolling back to red significances on submit

type MyAccountQuestionnaireDefaultsData = {
  flattenedQuestionnaire: any[]; //questionareDefauls doesn't have questionnaire mention, so he has to provied flattend and questioAnswer
  questionAnswersObject: any[];
  country: string;
  language: string;
};

interface QuestionnaireProps {
  myAccountQuestionnaireDefaults?: MyAccountQuestionnaireDefaultsData | null  // if it came from my account
  submitButtonText?:string;
  onSubmitOverride?: (wasRefilled:boolean, bundledQuestionnaireForSending: any, files: Array<any>) => void; // overriding the default onSubmit call that creates cart and etc...
  overrideQuestionnaireParams?:any;
  prefilledQuestionnaire?:any;
  navigationOverride?: () => void;
  disableSkipQuestionnaireModal?: boolean; // if true, the skip questionnaire modal will not be shown
  hideHeaderAndLogin?: boolean; // if true, the header progress bar and login question will be hidden
  enableSaveAnswers?: boolean; // opt-in to the save/restore-answers experiment for an embedded flow (e.g. reorder), so Group B users still get localStorage persistence
}

// Mirrors QuestionnaireVariantB's conditional_answer_id gate (computeNextPage /
// computeRevealedForSubmit): a question is only shown/required/submitted once
// the answer it depends on is currently selected.
const isQuestionGateSatisfied = (
  question: any,
  answersSelectedMap: Record<string | number, any>
): boolean =>
  !question?.conditional_answer_id || Boolean(answersSelectedMap[question.conditional_answer_id]);

const Questionnaire: React.FC<QuestionnaireProps> = ({submitButtonText=null, overrideQuestionnaireParams = null, myAccountQuestionnaireDefaults = null, onSubmitOverride = null,prefilledQuestionnaire = null, navigationOverride = null, disableSkipQuestionnaireModal = false, hideHeaderAndLogin = false, enableSaveAnswers = false }) => {
  const navigate = useNavigate();
  const [searchParamas] = useSearchParams();
  const { saveCart, updateCart } = useCartContext();
  let { country, category, language } = useParams();
  country = myAccountQuestionnaireDefaults || overrideQuestionnaireParams ? myAccountQuestionnaireDefaults?.country || overrideQuestionnaireParams.country : country;
  language = myAccountQuestionnaireDefaults || overrideQuestionnaireParams ? myAccountQuestionnaireDefaults?.language || overrideQuestionnaireParams.language : language;
  category = overrideQuestionnaireParams ? overrideQuestionnaireParams.optionalCategory : category;

  const { customerState } = useCustomer();
  const urlParams = new URLSearchParams(window.location.search);
  const catalogID = urlParams.get("catalog_id");
  const selfPickUpPharmacyID = urlParams.get("sppi"); //Self pickup pharmacy ID
  const cameFromOutOfStockBanner = urlParams.has("came_from_out_of_stock_banner");

  // ---------------------------------------------- questionnaire data  ----------------------------------------------
  const questionnaireAttributes = useQuestionnaire();

  // -------------------------------------------------------------------------------------------------------------
  const [filesMap, setFilesMap] = useState<Record<number, any[]>>({});
  const [mention, setMention] = useState<any>(category);
  const [loading, setLoading] = useState<boolean>(myAccountQuestionnaireDefaults ? false : true);
  const [postLoading,setPostLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(false);
  const [questionnaireTitle, setQuestionnaireTitle] = useState("");
  const [autoDiscount, setAutoDiscount] = useState<any>(null);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [submitButtonLoading, setSubmitButtonLoading] = useState<boolean>(false);
  const [disabledQuestionIDs, setDisabledQuestionIDs] = useState<number[]>([]);
  const [showStickyNotification, setShowStickyNotification] = useState({
    show: false,
    title: "",
    content: "",
    level: "error",
  });
  const [categoryCatalog, setCategoryCatalog] = useState<{id: number, mention: string} | null>(null);
  const [customerZipcode, setCustomerZipcode] = useState<string>("");
  const [bmiValidationFailed, setBmiValidationFailed] = useState<boolean>(false);
  const [uploadValidationFailed, setUploadValidationFailed] = useState<boolean>(false);
  // When a returning customer's questionnaire is pre-filled, file/image uploads can't be
  // pre-filled — flag set on load so we mark the required upload question(s) red and jump to them.
  const [shouldRemindUploadPrefill, setShouldRemindUploadPrefill] = useState<boolean>(false);
  const [isFirstTimeCustomer, setIsFirstTimeCustomer] = useState<boolean | null>(null);

  // ---- save_questionnaire_answers A/B test ----
  // The variant is controlled by the `save_questionnaire_answers_ab_test`
  // variable in the variables table. It is assigned once from the server
  // response weight, then persisted by ABTestManager in localStorage.
  // Embedded flows (My Account renew, subscription renew, Schengen) are
  // excluded from save/restore but still receive the variant in analytics.
  const isMainFunnel: boolean =
    !myAccountQuestionnaireDefaults &&
    !overrideQuestionnaireParams &&
    !onSubmitOverride &&
    mention !== "schengen";
  // Reorder is an embedded flow (overrideQuestionnaireParams is set) but opts in
  // via `enableSaveAnswers`, so Group B reorder customers still get save/restore.
  const isSaveAnswerEligible: boolean = isMainFunnel || enableSaveAnswers;
  const [saveAnswerVariant, setSaveAnswerVariant] = useState<SaveAnswerVariant>(getSaveAnswerVariant());
  const isSaveAnswerGroupB: boolean = isSaveAnswerEligible && saveAnswerVariant === "B";

  // ---- Variant B questionnaire (page-per-question) ----
  // HL-12595: the questionnaire_format A/B test (HL-9768) closed with Variant B, so every eligible
  // visitor gets it; everyone else keeps the one-page questionnaire. Eligibility follows the
  // catalog's own opt-in — catalog_choose_treatment_settings.layout === "care_plus", the same
  // switch ChooseTreatmentRouter uses to pick the Care+ treatment page. Driving it from the
  // setting instead of a hardcoded mention/country means enabling Care+ for another catalog or
  // market needs no code change, and the funnel can't get half-Care+ (Care+ treatment page but
  // legacy questionnaire, or vice versa). Product-catalog URLs (/wegovy, /mounjaro) are covered too:
  // the setting is read from their category catalog.
  const [isCarePlusCatalog, setIsCarePlusCatalog] = useState<boolean>(false);
  const isWeightLoss2Mention = (m: unknown): boolean => m === "weight_loss_2";
  const isWeightLoss3Mention = (m: unknown): boolean => m === "weight_loss_3";
  const isQuestionnaireFormatEligible: boolean = isMainFunnel && isCarePlusCatalog;
  const saveDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks when the user last interacted (or the page loaded) to compute dwell time per question.
  const questionDwellTimeRef = useRef<number>(Date.now());

  const showFullQuestionnaire = useMemo(() => {
    if (!isMainFunnel) return true;
    if (customerState) return true;
    return isFirstTimeCustomer === true;
  }, [isMainFunnel, customerState, isFirstTimeCustomer]);

  const questionnaireSubTitle = useMemo(() => {
    if (mention === "schengen") {
      return translator.translate(language,"default", "QuestionnairePage/schengen_sub_title");
    }
    
    return null;
    
  }, [mention])

  const isCannabisCategory = mention?.includes("medical_cannabis") ?? false;
  const isSwissCannabis = isCannabisCategory && country?.toUpperCase() === "CH";

  const questionnaireSubmitButtonText = useMemo(() => {
    if (submitButtonText){
      return submitButtonText;
    }

    if (mention === "schengen") {
      return translator.translate(language,"default", "QuestionnairePage/schengen_submit_button_text");
    }
    
    return translator.translate(language,"default", "QuestionnairePage/submit_button_text");
    
  }, [mention])



  const dangerSignificancesRefsDispatch = (action: "add" | "remove", ref: any) => {
    if (action === "add") {
      dangerSignificancesRefs.push(ref);

    } else { // remove
      dangerSignificancesRefs = dangerSignificancesRefs.filter((existingRef: any) => existingRef.id !== ref.id);
    }
  }


  const handleErrorNotification = (title: string, content: any) => {
    if (!title && !content) {
      setShowStickyNotification({ show: false, level: "error", title: "", content: "" });
    } else {
      setShowStickyNotification({ show: true, level: "error", title, content });
    }
  };


  useEffect(() => {

    if (myAccountQuestionnaireDefaults) {
      questionnaireAttributes.setQuestionnaireTree(myAccountQuestionnaireDefaults.questionAnswersObject);
      questionnaireAttributes.setFlattenedQuestionnaire(myAccountQuestionnaireDefaults.flattenedQuestionnaire);

    } else {

      const fetchingStartTime = new Date().getTime();

      window.eventsTracking.track({e: 'Questionnaire loading started'});
      let fetchFinished = false
      // Set when the UK split sends the visitor to the other funnel: keep the loader up until the replace lands.
      let redirectingFunnel = false

      setTimeout(()=>{
        if (!fetchFinished){
          MainTracker.track('custom', 'Questionnaire loading 3sec exceeded');
        }
      },3000)

      //Regular from questionnaire page
      http
        .getFromServer(
          `questionnaire/${country}/${language}/${mention}?customerVerificationRequired=${searchParamas.get("cv") === 'true'}${getCartMentionQuery(mention)}`,
          [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER]
        )
        .then((response) => {
          // UK WL1 vs WL2 split: the WL2 share goes to weight_loss_2 before anything renders or tracks.
          // Main funnel only: reorder / renew / My Account questionnaires have no weight_loss_2 route.
          const splitUrl = isMainFunnel ? getFunnelSplitRedirectUrl(response.data.funnelSplit, mention, window.location) : null;
          if (isMainFunnel) registerSplitFunnelVariant(response.data.funnelSplit, splitUrl);
          if (splitUrl) {
            redirectingFunnel = true;
            window.location.replace(splitUrl);
            return;
          }
          const fetchingTotalTime = new Date().getTime() - fetchingStartTime;
          questionnaireAttributes.setQuestionnaireTree(response.data.questionAnswersObject);
          questionnaireAttributes.setFlattenedQuestionnaire(response.data.flattenedQuestionnaire);

          setDisabledQuestionIDs(response.data?.disabledQuestionIDs ?? []);

          setMention(response.data.categoryMention);
          setCategoryCatalog({ id: response.data.categoryID, mention: response.data.categoryMention });
          setQuestionnaireTitle(response.data.questionnaireTitle);
          const serverPrefilled = (response.data.prefilledQuestionnaire?.length ?? 0) > 0;
          prefillQuestionnaire(response.data.prefilledQuestionnaire)
          let localRestored = false;
          if (isSaveAnswerGroupB && typeof country === "string" && typeof response.data.categoryID === "number") {
            localRestored = restoreLocalSavedAnswers(
              country,
              response.data.categoryID,
              response.data.flattenedQuestionnaire as FlattenedQuestion[],
              response.data.questionAnswersObject as Record<string | number, AnswerTreeNode>
            );
          }
          // Uploads are never pre-filled — if anything else was, remind the customer to re-upload.
          if (serverPrefilled || localRestored) {
            setShouldRemindUploadPrefill(true);
          }
          setAutoDiscount(response.data.autoDiscount);
          MainTracker.superProperties({ "Category Catalog": response.data.categoryShortcut, "Mixpanel Category Catalog": response.data.categoryMixpanelTitle });
          // HL-10959: keep Care+ A/B arm + entry_point on all subsequent WL events
          registerWlExperimentSuperProperties();
          window.eventsTracking.track({e: 'Questionnaire loading ok',p:{ Duration: String(fetchingTotalTime / 1000) }});
          SocialMediaTracker.track("pv", QUESTIONNAIRE_PAGE_VIEW_EVENT, {mention, searchParamas: Object.fromEntries(searchParamas.entries()) });

          // Track questionnaire start in GTM
          trackGTM("custom", "ga4_auto_event", {
            'event_name': 'medical_questions_start',
            'funnel_step': 'funnel_Step_3',
            'location': '3', // TODO: Determine what location values 1/2/3/4 represent
            'subcategory': response.data.categoryMention
            // category will be auto-added by GtmTracker based on subcategory
          });
          
          const saveAnswerAbTestWeight = Number(response.data.saveQuestionnaireAnswersABTestVariable) || 0;
          const assignedSaveAnswerVariant = assignSaveAnswerVariant(saveAnswerAbTestWeight);
          setSaveAnswerVariant(assignedSaveAnswerVariant);

          setIsCarePlusCatalog(response.data.catalogSettings?.layout === "care_plus");

          // Read from the response: the `mention` state may still hold the raw route param in this tick.
          const isWeightLoss2 = isWeightLoss2Mention(response.data.categoryMention);
          const isWeightLoss3 = isWeightLoss3Mention(response.data.categoryMention);

          // HL-12122: customer_status (new | returning | active_subscriber) on every later event.
          registerCustomerStatus(response.data.customerStatus);
          // HL-11440 §11: qversion rides on every later DE WL event (v2 = WL3, control = the WL2 questionnaire,
          // which product-catalog URLs like /wegovy still load). The UK's weight_loss_2 is its V3 questionnaire.
          const isUk = country?.toUpperCase() === "GB";
          if (!isUk && (isWeightLoss2 || isWeightLoss3)) {
            MainTracker.superProperties({ qversion: isWeightLoss3 ? "v2" : "control" });
          }

          // HL-12122: entry_route / entry_page (bridge, category, product page, CRM email…) on every later WL event.
          // The V3 questionnaire: DE weight_loss_3, UK weight_loss_2.
          if (isMainFunnel && (isWeightLoss3 || (isUk && isWeightLoss2))) {
            registerFunnelEntry();
          }

          const showsOneQuestion = isMainFunnel && !customerState;
          MainTracker.track("pv", "View Questionnaire Page", {
            "One question?": showsOneQuestion ? "Yes" : "No",
            save_answer: assignedSaveAnswerVariant,
            ...getFlowzzTrackingProps(),
          });

          if (response.data?.customerZipcode) {
            setCustomerZipcode(response.data.customerZipcode);
          }

          if (response.data?.shouldIDVerify) {
            updateCart({
              should_id_verify: response.data.shouldIDVerify
            })
          }
        })
        .catch((err) => {

          if (err?.response?.status === 403 && mention === "schengen")  {

            navigate({
                  pathname: `/${country}/login`,
                  search: "redirect=" + window.location.pathname,
                });
                return;

          }




          window.clientLogger.error("error getting data in questionnaire", err)
          const fetchingTotalTime = new Date().getTime() - fetchingStartTime;
          MainTracker.track('custom', 'Questionnaire loading failed',  { Duration: String(fetchingTotalTime / 1000) } );
          setError(500);
        })
        .finally(() => {
          if (!redirectingFunnel) setLoading(false);
          fetchFinished = true
        })
    }


    if(prefilledQuestionnaire){
      prefillQuestionnaire(prefilledQuestionnaire);
      if (prefilledQuestionnaire.length > 0) {
        setShouldRemindUploadPrefill(true);
      }
    }


  }, []);


  // On a pre-filled (returning customer) questionnaire, file/image uploads can't be restored.
  // Once the page has rendered, mark the required upload question(s) red and jump to the first one
  // so the customer realizes they need to re-upload. One-shot (clears itself after running).
  useEffect(() => {
    if (loading || !shouldRemindUploadPrefill) return;

    const uploadQuestion = findRequiredUploadQuestion();
    if (uploadQuestion) {
      setUploadValidationFailed(true);
      setTimeout(() => {
        document
          .getElementById("question_" + uploadQuestion.id)
          ?.scrollIntoView({ behavior: "smooth" });
      }, 150);
    }

    setShouldRemindUploadPrefill(false);
  }, [loading, shouldRemindUploadPrefill]);



  const prefillQuestionnaire = (prefilledQuestionnaire: any[]) => {

    prefilledQuestionnaire.forEach((questionAnswerPair: any) => {
      switch (questionAnswerPair.recipe.type) {
        case "textarea":
        case "date":
        case "number":
          onTextChange(
            questionAnswerPair.recipe.recipe_answer_id,
            questionAnswerPair.recipe.value,
            questionAnswerPair.recipe.validation_rule,
            questionAnswerPair.recipe.recipe_question_id 
          );
          break;
        
        case "check_list":
          let activeChildern = questionAnswerPair.children?.filter((child : any) => {
            return questionAnswerPair.recipe.recipe_answer_id.includes(child.recipe.id)
          })

          if (activeChildern) {
            for (let child of activeChildern) {
              onAnswerClick(child, true);
            }
          }

          break;

        default:
          onAnswerClick(questionAnswerPair, true);
          break;
      }
    })
  }

  /**
   * Restore previously-saved answers for Group B users (main funnel only).
   * Runs AFTER the server's `prefillQuestionnaire(...)`, so local values
   * overlay the server prefill. The pure diff (which keys to clear, which
   * values to apply) lives in `computeRestorePlan`; here we only apply that
   * plan to component state:
   *   1. clear every answer key in restored questions' subtrees (so
   *      server-prefilled siblings or checklist options don't leak through)
   *   2. apply the saved values via the existing onAnswerClick / onTextChange
   *      so reveal-state + validation map stay correct.
   * Returns true when at least one value was applied.
   */
  const restoreLocalSavedAnswers = (
    countryID: string,
    categoryID: number,
    freshFlattened: FlattenedQuestion[],
    freshTreeRoots: Record<string | number, AnswerTreeNode>
  ): boolean => {
    const plan = computeRestorePlan(countryID, categoryID, freshFlattened, freshTreeRoots);
    if (!plan) return false;

    // Clear server-prefilled state for every answer in restored questions
    // first, so leftover `true`s (checklist options, etc.) don't survive.
    questionnaireAttributes.setAnswersSelectedMap((form: Record<string | number, AnswerValue>) => {
      const next: Record<string | number, AnswerValue> = { ...form };
      for (const id of plan.idsToClear) {
        delete next[id];
      }
      return next;
    });

    for (const { node, value } of plan.valuesToApply) {
      if (typeof value === "boolean") {
        if (value === true) {
          onAnswerClick(node, true);
        }
      } else {
        onTextChange(
          node.recipe.id,
          String(value),
          node.recipe.validation_rule,
          node.recipe.recipe_question_id
        );
      }
    }

    return plan.valuesToApply.length > 0;
  };


  const onTextChange = (answerId: number, text: String | null, validationRule: any, recipeQuestionId: number | null) => {
    if (text && !questionnaireAttributes.answersSelectedMap[answerId]) {
      //fire event only where there is text and its the first time the answers object is changing
      const time_on_page_ms = Date.now() - questionDwellTimeRef.current;
      questionDwellTimeRef.current = Date.now();
      window.eventsTracking.track({e:'Click On Answer',p:{ 'Question id': recipeQuestionId, question_id: recipeQuestionId, 'Answer id': answerId, time_on_page_ms }});
    }


    
    questionnaireAttributes.setAnswersSelectedMap(
      (form: any) => ({
        ...form,
        [answerId]: text,
      })
    )

    //update the validation object
    let isInputValid = true;

    //if there is an upper bound
    if (validationRule && validationRule.max_value !== undefined) {
      isInputValid = Number(text) <= validationRule.max_value;
    }

    //if there is a lower bound
    if (validationRule && validationRule.min_value !== undefined) {
      isInputValid = isInputValid && validationRule.min_value <= Number(text);
    }

    questionnaireAttributes.setQuestionnaireValidationObject(
      (oldVal: any) => {
        return {
          ...oldVal,
          [answerId]: isInputValid,
        };
      }
    );
  }

  const onAnswerClick = (answer: any, value: any) => {
    const time_on_page_ms = Date.now() - questionDwellTimeRef.current;
    questionDwellTimeRef.current = Date.now();
    window.eventsTracking.track({e:'Click On Answer',p:{ 'Question id': answer.recipe.recipe_question_id, question_id: answer.recipe.recipe_question_id, 'Answer id': answer.recipe.id, time_on_page_ms }});
    let valueForSaving = true;
    if (value || value === 0) {
      valueForSaving = value;
    }
    //save the answer
    questionnaireAttributes.setAnswersSelectedMap(
      (form: any) => ({
        ...form,
        [answer.recipe.id]: valueForSaving,
      })
    );

    //update the validation object
    questionnaireAttributes.setQuestionnaireValidationObject(
      (oldVal: any) => {
        let isValid = true;
        let idToUpdate = answer.recipe.type === "number" ? answer.recipe.id : answer.recipe.parent_id;
        if (answer.recipe.validation_rule.max_value) {
          isValid = answer.recipe.validation_rule.min_value <= Number(value) && Number(value) <= answer.recipe.validation_rule.max_value;
        }

        return {
          ...oldVal,
          [idToUpdate]: isValid,

        };
      }
    );

    //mark siblings as unselected
    questionnaireAttributes.shutDownSiblingsDescendants(answer);
  }

  // Group B only, main funnel only: debounce-persist the latest answer state
  // to localStorage so a Back-button / forward navigation can restore it on
  // the next mount. We never persist upload File objects.
  useEffect(() => {
    if (!isSaveAnswerGroupB) return;
    if (!questionnaireAttributes.flattenedQuestionnaire.length) return;
    if (!categoryCatalog || !Number.isFinite(categoryCatalog.id)) return;
    if (typeof country !== "string") return;

    // Capture the narrowed values so TS keeps the types inside the setTimeout
    // closure (otherwise `country` widens back to `string | undefined`).
    const countryID: string = country;
    const categoryID: number = categoryCatalog.id;
    const categoryMention: string = categoryCatalog.mention;

    if (saveDebounceTimerRef.current !== null) {
      clearTimeout(saveDebounceTimerRef.current);
    }
    saveDebounceTimerRef.current = setTimeout(() => {
      saveDebounceTimerRef.current = null;
      const flattened = questionnaireAttributes.flattenedQuestionnaire as FlattenedQuestion[];
      const map = questionnaireAttributes.answersSelectedMap as Record<string | number, AnswerValue | null | undefined>;
      const questions = serializeQuestionnaireAnswers(flattened, map);
      saveQuestionnaireAnswers({
        countryID,
        categoryID,
        categoryMention,
        questions,
      });
    }, 400);

    return () => {
      if (saveDebounceTimerRef.current !== null) {
        clearTimeout(saveDebounceTimerRef.current);
        saveDebounceTimerRef.current = null;
      }
    };
  }, [
    isSaveAnswerGroupB,
    questionnaireAttributes.answersSelectedMap,
    questionnaireAttributes.flattenedQuestionnaire,
    categoryCatalog,
    country,
  ]);

  const isPostCheckoutQuestionHidden = (q: any) => q.is_post_checkout_question;

  // Find the first visible, required file/image upload question (top-level or revealed subquestion).
  // Shared by submit validation and the load-time "re-upload" reminder for returning customers.
  const findRequiredUploadQuestion = () =>
    questionnaireAttributes.flattenedQuestionnaire.find((q: any) => {
      if (isPostCheckoutQuestionHidden(q)) return false;
      if (!isQuestionGateSatisfied(q, questionnaireAttributes.answersSelectedMap)) return false;

      const agType = q.answersGroup?.recipe?.type;
      const agRequired = q.answersGroup?.recipe?.validation_rule?.required === true;

      if (q.question_type === "photo_diagnose" && agRequired) return true;
      if (agType === "photo_diagnose" && agRequired) return true;
      if (agType === "document_upload" && agRequired) return true;

      return q.additional?.some(
        (sub: any) =>
          questionnaireAttributes.revealedAnswerGroups[sub.recipe?.id] &&
          (sub.recipe?.type === "photo_diagnose" || sub.recipe?.type === "document_upload") &&
          sub.recipe?.validation_rule?.required === true
      );
    });

  const checkQuestionnaireValidity = (): { isValid: boolean; questionnaire: any; bmi?: number | null } => {
    const visibleFlattenedQuestionnaire = questionnaireAttributes.flattenedQuestionnaire
      .filter((q: any) => !isPostCheckoutQuestionHidden(q));

    let questionnaire = bundleQuestionnaireForSending(
      visibleFlattenedQuestionnaire,
      questionnaireAttributes.answersSelectedMap,
      questionnaireAttributes.revealedAnswerGroups
    );

    //format the answers to match backend structure for orderPrescriptionQuestions table in DB
    questionnaire = formatQuestionnaire(
      questionnaire,
      questionnaireAttributes.answersSelectedMap
    );
    
    let isValid = questionnaireAttributes.validateQuestionnaire(
      questionnaire,
      questionnaireAttributes.revealedAnswerGroups,
      questionnaireAttributes.questionnaireValidationObject
    );

    let bmi: number | null = null;

    // BMI three-tier validation for bmi_question type
    {
      const bmiQuestion = questionnaireAttributes.flattenedQuestionnaire.find(
        (q: any) => q.question_type === "bmi_question"
      );

      if (bmiQuestion) {
        const heightAnswerId = bmiQuestion.answersGroup?.recipe?.id;
        const weightAnswerId = bmiQuestion.answersGroup?.children?.[0]?.recipe?.id;
        const heightVal = Number(questionnaireAttributes.answersSelectedMap[heightAnswerId]) || 0;
        const weightVal = Number(questionnaireAttributes.answersSelectedMap[weightAnswerId]) || 0;
        bmi = calculateBMI(weightVal, heightVal);

        const { comorbidity: comorbidityThreshold, minimum: minimumThreshold } = extractBmiThresholds(bmiQuestion);

        // Compute comorbidity using explicit answer group ID
        const comorbidityAnswerGroupID = bmiQuestion.answersGroup?.recipe?.validation_rule?.bmi_comorbidity_answer_group_id;
        let hasComorbidity = false;
        if (comorbidityAnswerGroupID) {
          const comorbidityQuestion = questionnaireAttributes.flattenedQuestionnaire.find(
            (q: any) => q.answersGroup?.recipe?.id === comorbidityAnswerGroupID
          );
          if (comorbidityQuestion) {
            const children = comorbidityQuestion.answersGroup?.children ?? [];
            const illnessOptions = children.slice(0, -1);
            hasComorbidity = illnessOptions.some((child: any) => questionnaireAttributes.answersSelectedMap[child.recipe.id]);
          }
        }

        if (!bmi || bmi < comorbidityThreshold) {
          isValid = false;
          setBmiValidationFailed(true);
          setTimeout(() => {
            document.getElementById("bmi-section")?.scrollIntoView({ behavior: "smooth" });
          }, 150);
        } else if (bmi <= minimumThreshold) {
          if (!hasComorbidity) {
            isValid = false;
            setBmiValidationFailed(true);
            setTimeout(() => {
              document.getElementById("bmi-section")?.scrollIntoView({ behavior: "smooth" });
            }, 150);
          } else {
            setBmiValidationFailed(false);
          }
        } else {
          setBmiValidationFailed(false);
        }
      }
    }

    // Upload question validation — require at least one file when a required upload question is visible
    {
      const uploadQuestion = findRequiredUploadQuestion();

      if (uploadQuestion && (filesMap[uploadQuestion.id] || []).length === 0) {
        isValid = false;
        setUploadValidationFailed(true);
        setTimeout(() => {
          document.getElementById("question_" + uploadQuestion.id)?.scrollIntoView({ behavior: "smooth" });
        }, 150);
      } else if (uploadQuestion) {
        setUploadValidationFailed(false);
      }
    }

    return { isValid, questionnaire, bmi };
  };
  

  const onSubmit = async (zip: string = customerZipcode) => {
    //default incase we dont get onSubmit as a prop
    setSubmitButtonLoading(true);
    //combine the questions and answer information to one single object
    MainTracker.track("click", "Click Submit Questionnaire");

    //IF THERE ARE ANY DANGER (RED) SIGNIFICANCES WE SCROLL BACK
    if (dangerSignificancesRefs.length > 0) {
      MainTracker.track("click", "Click Submit Questionnaire Validation Failed");
      setTimeout(() => {
        dangerSignificancesRefs[0].current.scrollIntoView({
          behavior: "smooth",
        });
      }, 150);
      setSubmitButtonLoading(false);
      return;
    }

    const { isValid, questionnaire, bmi } = checkQuestionnaireValidity();

    if (bmi != null) {
      const bmiGroup = bmi < 27 ? 1 : bmi < 30 ? 2 : 3;
      MainTracker.track('custom', 'BMI Validation', { bmi, bmiGroup });
    }

    //this suggests the user answered a blocking answers, and should be redirected to /evaluation
    let isBlocking: Boolean = false;

    if (isValid) {

      const allFiles = Object.entries(filesMap).flatMap(([questionId, questionFiles]) =>
        questionFiles.map((f: any) => ({ ...f, question_id: Number(questionId) }))
      );

      if (onSubmitOverride) {
        onSubmitOverride(true, questionnaire, allFiles);
        setSubmitButtonLoading(false);

      } else {

        isBlocking = isQuestionaireBlocking(
          questionnaire,
          questionnaireAttributes.answersSelectedMap
        );

        setPostLoading(true);
        let isExpressReorder = window.location.href.includes("reorder") && window.location.href.includes("medical_disclaimer");
        const selectedConsultationType = isSwissCannabis
          ? "video"
          : ((searchParamas.get("ct") as "video" | "text") || "text");

        http
          .postMultiPartsToServer(
            `questionnaire/${country}/${language}/${mention}?${searchParamas.toString()}`,
            {
              questionnaire: questionnaire,
              isBlocking,
              country: country,
              language: language,
              mention,
              allowOnlyPrescription: country?.toLowerCase() === "fr" || cameFromOutOfStockBanner,
              isExpressReorder,
              selfPickUpPharmacyID,
              zipcode: zip,
              selectedConsultationType,
              customerVerificationRequired: searchParamas.get("cv") === 'true',
              fileQuestionIds: allFiles.map((f: any) => f.question_id),
            },
            allFiles,
            [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER]
          )
          .then((res) => {
            // HL-12122: last question submitted and saved.
            MainTracker.track("custom", "questionnaire_completed", { mention, is_blocking: Boolean(isBlocking) });
            saveCart({
              id: res.data.cartID,
              token: res.data.cartToken,
              mention,
              mixpanel_title: res.data.catalog.mixpanel_title,
              products:[],
              timekitBooking: null,
              consultation_type: selectedConsultationType,
              is_follow_up_consultation: res.data.is_follow_up_consultation ? 1 : 0,
              consultation_fee: res.data.consultation_fee ?? undefined,
              customer_verification_type: res.data.customerVerificationType,
              should_id_verify: res.data.shouldIDVerify
            })

            // Drop a marker so the Thankyou page (= real order placement)
            // can clean up the saved entry for this questionnaire. Only flows
            // that actually persisted answers (main funnel + opted-in reorder).
            if (isSaveAnswerEligible && categoryCatalog && Number.isFinite(categoryCatalog.id) && typeof country === "string") {
              markPendingClear(country, categoryCatalog.id);
            }

            const catalogSettings = JSON.parse(res.data.catalog.settings ?? '{}');


            // Track questionnaire submission in GTM
            trackGTM("custom", "ga4_auto_event", {
              'event_name': 'medical_questions_submit',
              'funnel_step': 'funnel_Step_4',
              'location': '3',
              'subcategory': mention
              // category will be auto-added by GtmTracker based on subcategory
            });

            // Clean up OAuth-related parameters from search params
            const cleanedSearchParams = getForwardableFunnelSearch(searchParamas);
            let newSearchParams = cleanedSearchParams.toString()
            
            if (isBlocking) {
              navigate({
                pathname: `/evaluation/${country}/${language}`,
              });

            } else if (navigationOverride){
              navigationOverride()
            }
             else if (
              res.data.catalog &&
              (res.data.catalog.is_video_consultation ||
              catalogSettings.skip_choose_treatment)
            ) {

              // if customer logged in, then send directly to shipping
              
              if (customerState?.customerID) {
                navigate({
                  pathname: `/shipping/${country}/${language}`,
                  search: newSearchParams ? `?${newSearchParams}` : ''
                });
              }
              else {
                // send to checkout
                navigate({
                  pathname: `/sign_up/${country}/${language}`,
                  search: newSearchParams ? `?${newSearchParams}` : ''
                });
              }
            } else if (isSwissCannabis && !customerState?.customerID) {
              const afterLoginPath = res.data.shouldIDVerify
                ? `/customer-verification/${country}/${language}`
                : `/select_treatment/${country}/${language}/${mention}`;
              const redirectParam = encodeURIComponent(afterLoginPath + (newSearchParams ? `?${newSearchParams}` : ''));
              navigate({
                pathname: `/${country}/login`,
                search: `redirect=${redirectParam}`
              });
            } else if (isSwissCannabis && res.data.shouldIDVerify) {
              navigate({
                pathname: `/customer-verification/${country}/${language}`,
                search: newSearchParams ? `?${newSearchParams}` : ''
              });
            } else {
              let path = `/select_treatment/${country}/${language}/${mention}`;
              
              const catalogParam = catalogID && !searchParamas.get("catalog_id") ? `catalog_id=${catalogID}` : "";
              newSearchParams = [cleanedSearchParams.toString(), catalogParam].filter(p => p).join('&')

              navigate({
                pathname: path,
                search: newSearchParams ? `?${newSearchParams}` : ''
              });
            }
          })
          .catch((err) => {
            handleErrorNotification(
              translator.translate(
                language,
                "default",
                "StickyNotification/danger_title"
              ),
              "Server Failed"
            );
          })
          .finally(() => {
            setSubmitButtonLoading(false);
            setPostLoading(false);
          });
      }

    } else {
      MainTracker.track("click", "Click Submit Questionnaire Validation Failed");
      setSubmitButtonLoading(false);
      setPostLoading(false);
    }
  };


  if (isQuestionnaireFormatEligible) {
    return <QuestionnaireVariantB />;
  }

  return (
    <>
      {loading && <LoadingModal />}

      {error && !loading && (
        <>
          <HeaderController showSteps={false} stage={"Questionnaire"} isCannabis={isCannabisCategory} />
          <ServerError status={500} />
        </>
      )}

      {!loading && !error && (
        <div className="questionnaire-page-wrapper">
          <StickyNotification
            showNotification={showStickyNotification.show}
            title={showStickyNotification.title}
            onClick={(falseValue: any) => setShowStickyNotification(falseValue)}
            content={showStickyNotification.content}
            level={showStickyNotification.level}
          />

          {/* if state true -> Show forgot password overlay and send component closeModal property */}
          {showForgotPasswordModal && (
            <ForgotPasswordModal
              closeModal={() => setShowForgotPasswordModal(false)}
              language={language!}
              setStickyNotificationDetails={setShowStickyNotification}
            />
          )}

          {!hideHeaderAndLogin && (
            <HeaderController
              language={language}
              stage="Questionnaire"
              isCannabis={isCannabisCategory}
            />
          )}

          {/* if state true -> Show forgot password overlay and send component closeModal property */}

          <div className="questionnaire-body-container">
            {postLoading && <DarkLoadingModal />}
            
            {!disableSkipQuestionnaireModal && (
              <SkipQuestionnaireModal categoryCatalog={categoryCatalog} language={language} country={country} skipModals={searchParamas.get("skipQuestionnaire")} askOncePerSession={isCarePlusCatalog} />
            )}

            <div className="questionnaire-treatment-form-container">
              {autoDiscount && autoDiscount.discount_type !== "free" && language!.toLowerCase() === "de" ?
                <EmojiBlock
                  discount_measure={autoDiscount.discount_measure}
                  discount_value={autoDiscount.discount_value}
                /> : null
              }
              <h1 className="questionnaire-title">{questionnaireTitle}</h1>
              {Boolean(questionnaireSubTitle) && <h2 className="questionnaire-subtitle">{questionnaireSubTitle}</h2>}
              <div className="questionnaire-container">
                {!hideHeaderAndLogin && (
                  <LoginQuestion
                    showForgotPasswordModal={() =>
                      setShowForgotPasswordModal(true)
                    }
                    language={language}
                    country={country}
                    onChange={(isFirstTime: Boolean) => {
                      if (isFirstTime) {
                        window.eventsTracking.track({e: 'Clicked on First Time Order Question'});
                      }
                    }}
                    onFirstTimeAnswerChange={(isFirstTime: boolean) => setIsFirstTimeCustomer(isFirstTime)}
                    setCustomerZipcode={setCustomerZipcode}
                    OAuthRedirectPath={`questionnaire/${country}/${language}/${mention}?${searchParamas.toString()}`}
                    OAuthCameFromPath={`questionnaire/${country}/${language}/${mention}?${searchParamas.toString()}`}
                    showLoginWindowParam={searchParamas.get("showOauthAdditionalInfoModal") === "true"}
                    shouldScrollOnAnswer={isMainFunnel && !customerState}
                  />
                )}
                {showFullQuestionnaire && <form className="questionnaire-form-container" id="questionnaire-form" onSubmit={(e) => {
                    e.preventDefault();
                    onSubmit();
                }}>
                  {/* start rending every question in the flatterened array */}
                  {questionnaireAttributes.flattenedQuestionnaire
                    .filter((question: any) => {
                      if (!isQuestionGateSatisfied(question, questionnaireAttributes.answersSelectedMap)) return false;
                      if (isPostCheckoutQuestionHidden(question)) return false;
                      return true;
                    })
                    .map(
                    (question: any) => (
                      <Question
                        handleRef={questionnaireAttributes.handleRef}
                        onReveal={questionnaireAttributes.onReveal}
                        onHide={questionnaireAttributes.onHide}
                        key={question.id}
                        formData={questionnaireAttributes.answersSelectedMap}
                        onAnswerClick={onAnswerClick}
                        onTextChange={onTextChange}
                        question={question}
                        files={filesMap[question.id] || []}
                        setFiles={(newFiles: any[]) => setFilesMap((prev) => ({ ...prev, [question.id]: newFiles }))}
                        questionnaireValidationObject={
                          questionnaireAttributes.questionnaireValidationObject
                        }
                        dangerSignificancesRefsDispatch={dangerSignificancesRefsDispatch}
                        answersSelectedMap={questionnaireAttributes.answersSelectedMap}
                        mention={mention}
                        disabled={disabledQuestionIDs?.includes(Number(question.id)) ?? false}
                        flattenedQuestionnaire={questionnaireAttributes.flattenedQuestionnaire}
                        bmiValidationFailed={bmiValidationFailed}
                        uploadValidationFailed={uploadValidationFailed}
                      />
                    )
                  )}
                  <SubmitButton
                    id="questionnaire-submit-button"
                    className="questionnaire-submitButton"
                    language={language}
                    placeholder={questionnaireSubmitButtonText}
                    showArrow={true}
                    width={"auto"}
                    margin="15px 0px"
                    loading={submitButtonLoading}
                  />
                </form>}
              </div>
            </div>
          </div>
          <Footer page="Questionnaire" language={language} />
        </div>
      )}
    </>
  );
};



export default Questionnaire;
